/**
 * Appwrite Auth Service — COMPATIBILITY SHIM
 *
 * This file now delegates to the new Clerk-based auth service
 * (src/services/auth.ts). It preserves the same function signatures so
 * that existing component imports continue to work without changes.
 *
 * Requirements: 5.1, 5.2
 */
import { apiGet } from '../lib/api-client';

// Admin email constant (kept for compatibility)
const ADMIN_EMAIL = 'patilsiddhesh23@gmail.com';

/**
 * Login with Google OAuth2 — now redirects to Clerk sign-in.
 * Note: Clerk uses its own sign-in UI, so this function is a stub
 * that signals components to use the Clerk-based login flow.
 */
export async function loginWithGoogle(successUrl = window.location.origin + '/', failureUrl = window.location.origin + '/login') {
  // With Clerk, OAuth is handled via ClerkProvider's built-in UI or
  // the useSignIn hook. This stub returns a signal for components
  // that still call this function.
  console.warn('loginWithGoogle is deprecated. Use Clerk sign-in flow instead.');
  return { alreadyAuthenticated: false, user: null, useClerkSignIn: true };
}

/**
 * Get current session user via Clerk.
 * Returns null if not logged in.
 */
export async function getCurrentUser() {
  try {
    const clerk = (window).Clerk;
    if (!clerk || !clerk.user) return null;

    const user = clerk.user;
    return {
      $id: user.id,
      email: user.primaryEmailAddress?.emailAddress || '',
      name: user.fullName || user.firstName || '',
      emailVerification: user.primaryEmailAddress?.verification?.status === 'verified',
      prefs: { avatar: user.imageUrl || '' },
    };
  } catch {
    return null;
  }
}

/**
 * Sign out current user via Clerk.
 */
export async function logout() {
  try {
    const clerk = (window).Clerk;
    if (clerk) await clerk.signOut();
  } catch (err) {
    console.error('Logout error:', err);
  }
}

/**
 * Check if user is admin
 */
export function isAdminEmail(email) {
  return email === ADMIN_EMAIL;
}

/**
 * Check if user is a villa owner by checking villas collection via API.
 */
export async function checkIsOwner(email) {
  try {
    const res = await apiGet('/villas', { ownerEmail: email, limit: 1 });
    return res.documents && res.documents.length > 0;
  } catch {
    return false;
  }
}

/**
 * Ensure user document exists in the users collection.
 * With Clerk, the /auth/sync endpoint handles this on sign-in.
 * This function fetches the user record from the API.
 */
export async function ensureUserDocument(appwriteUser) {
  try {
    const userDoc = await apiGet(`/users/${appwriteUser.$id || appwriteUser.id}`);
    return userDoc;
  } catch {
    // User doc doesn't exist yet — will be created by /auth/sync on next sign-in
    return {
      id: appwriteUser.$id || appwriteUser.id,
      email: appwriteUser.email,
      displayName: appwriteUser.name || appwriteUser.email?.split('@')[0] || 'User',
      photoURL: appwriteUser.prefs?.avatar || '',
      isNewUser: true,
    };
  }
}

/**
 * Get full user info — combines Clerk user + user doc + role checks.
 */
export async function getFullUserInfo() {
  const appwriteUser = await getCurrentUser();
  if (!appwriteUser) return { user: null, isAdmin: false, isOwner: false, userDoc: null };

  const userDoc = await ensureUserDocument(appwriteUser);
  const admin = isAdminEmail(appwriteUser.email);
  const owner = await checkIsOwner(appwriteUser.email);

  const user = {
    uid: appwriteUser.$id,
    email: appwriteUser.email,
    displayName: userDoc?.displayName || appwriteUser.name,
    photoURL: userDoc?.photoURL || '',
    ...userDoc,
  };

  return { user, isAdmin: admin, isOwner: owner, userDoc };
}
